import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ExperienceCard } from '@/components/ExperienceCard';
import { RichText } from '@/components/RichText';
import { getExperiencesByCollection } from '@/data/experiencesRepo';
import { useCollection } from '@/hooks/useCollections';
import { hasBio } from '@/types/types_collection';
import { experienceRegionLabels, type ExperienceType } from '@/types/types_experience';

/** Public collection detail page: hero + the experiences that belong to it. */
export function PageCollectionDetail() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const { collection, loading } = useCollection(collectionId);
  const [members, setMembers] = useState<ExperienceType[]>([]);

  useEffect(() => {
    if (!collectionId) return;
    let cancelled = false;
    getExperiencesByCollection(collectionId).then((list) => {
      if (!cancelled) setMembers(list);
    });
    return () => {
      cancelled = true;
    };
  }, [collectionId]);

  if (loading) {
    return <div className='tw:px-6 tw:py-24 tw:text-center tw:text-ed-stone'>Loading…</div>;
  }

  if (!collection) {
    return (
      <div className='tw:px-6 tw:py-24 tw:text-center'>
        <h1 className='tw:font-serif tw:text-3xl'>Collection not found</h1>
        <Link to='/catalog' className='tw:mt-4 tw:inline-block tw:text-ed-gold tw:underline'>
          Back to the collection
        </Link>
      </div>
    );
  }

  const bio = collection.bio;

  return (
    <article>
      {/* Hero */}
      <section className='tw:relative tw:flex tw:min-h-[60vh] tw:items-end tw:overflow-hidden'>
        <img
          src={collection.heroImage}
          alt={collection.name}
          className='tw:absolute tw:inset-0 tw:h-full tw:w-full tw:object-cover'
        />
        <div className='tw:absolute tw:inset-0 tw:bg-gradient-to-t tw:from-ed-black/80 tw:via-ed-black/30 tw:to-transparent' />
        <div className='tw:relative tw:mx-auto tw:w-full tw:max-w-6xl tw:px-6 tw:pb-14 tw:text-ed-cream'>
          <p className='tw:text-xs tw:tracking-[0.3em] tw:uppercase tw:text-ed-gold'>{collection.subtitle}</p>
          <h1 className='tw:mt-3 tw:font-serif tw:text-5xl tw:font-light tw:leading-tight'>
            {collection.name}
          </h1>
          <p className='tw:mt-4 tw:text-sm tw:tracking-wide tw:text-ed-cream/80'>
            {experienceRegionLabels[collection.region]} · {collection.location}
          </p>
        </div>
      </section>

      <div className='tw:mx-auto tw:max-w-6xl tw:px-6 tw:py-16'>
        <Link
          to='/catalog'
          className='tw:text-xs tw:tracking-[0.2em] tw:uppercase tw:text-ed-stone tw:hover:text-ed-gold'
        >
          ← Back to the collection
        </Link>

        {/* The person behind the collection, ahead of what the collection is. */}
        {hasBio(bio) && (
          <section
            className={
              bio.image
                ? 'tw:mt-10 tw:mb-4 tw:grid tw:gap-12 tw:md:grid-cols-2 tw:md:items-center'
                : 'tw:mt-10'
            }
          >
            {bio.image && (
              <div className='tw:relative'>
                <img
                  src={bio.image}
                  alt={bio.name}
                  className='tw:aspect-[3/4] tw:w-full tw:rounded-sm tw:object-cover'
                />
                {(bio.badgeValue || bio.badgeLabel) && (
                  <div className='tw:absolute tw:-bottom-6 tw:-right-6 tw:rounded-sm tw:bg-ed-gold tw:px-8 tw:py-4'>
                    <p className='tw:font-serif tw:text-3xl tw:text-ed-black'>{bio.badgeValue}</p>
                    <p className='tw:text-xs tw:uppercase tw:tracking-wider tw:text-ed-black/70'>
                      {bio.badgeLabel}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className={bio.image ? 'tw:mt-10 tw:md:mt-0' : undefined}>
              {bio.eyebrow && (
                <p className='tw:text-xs tw:tracking-[0.3em] tw:uppercase tw:text-ed-gold'>{bio.eyebrow}</p>
              )}
              {bio.name && (
                <h2 className='tw:mt-3 tw:font-serif tw:text-4xl tw:font-light tw:text-ed-black'>
                  {bio.name}
                </h2>
              )}
              {bio.secondaryName && (
                <p className='tw:mt-1 tw:font-serif tw:text-2xl tw:font-light tw:text-ed-stone'>
                  {bio.secondaryName}
                </p>
              )}
              <RichText html={bio.text} className='tw:mt-6 tw:leading-relaxed tw:text-ed-charcoal/80' />
            </div>
          </section>
        )}

        {members.length > 0 && (
          <section className='tw:mt-16'>
            <div className='tw:mb-12 tw:text-center'>
              {/* Fixed eyebrow, as on the static chef-jun-okada.html — the
                  title and subtitle under it are the per-collection copy. */}
              <p className='tw:text-xs tw:tracking-[0.4em] tw:uppercase tw:text-ed-gold'>The Collection</p>
              {collection.experiencesTitle && (
                <h2 className='tw:mt-6 tw:font-serif tw:text-4xl tw:font-light tw:text-ed-black'>
                  {collection.experiencesTitle}
                </h2>
              )}
              {collection.experiencesSubtitle && (
                <p className='tw:mx-auto tw:mt-4 tw:max-w-2xl tw:leading-relaxed tw:text-ed-stone'>
                  {collection.experiencesSubtitle}
                </p>
              )}
            </div>

            <div className='tw:grid tw:gap-8 tw:sm:grid-cols-2 tw:lg:grid-cols-3'>
              {members.map((member) => (
                <ExperienceCard key={member.id} experience={member} />
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}
